/**
 * WebSocket服务 - 管理与后端WebSocket服务器的连接
 * 接收实时图表数据和价格更新
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.url = 'ws://localhost:8001';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
    this.listeners = new Map();
    this.isConnecting = false;
  }

  /**
   * 连接WebSocket服务器
   */
  connect() {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;
    console.log('🔌 正在连接WebSocket服务器...');

    try {
      this.ws = new WebSocket(this.url);
      this.setupEventHandlers();
    } catch (error) {
      console.error('❌ WebSocket连接失败:', error);
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  /**
   * 设置WebSocket事件处理器
   */
  setupEventHandlers() {
    this.ws.onopen = () => {
      console.log('✅ WebSocket连接成功');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.emit('connected');
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // console.log('📨 收到WebSocket数据:', data);
        this.handleMessage(data);
      } catch (error) {
        console.error('❌ 解析WebSocket数据失败:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('🔌 WebSocket连接关闭:', event.code, event.reason);
      this.isConnecting = false;
      this.emit('disconnected');
      
      if (!event.wasClean) {
        this.handleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket错误:', error);
      this.isConnecting = false;
      this.emit('error', error);
    };
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(data) {
    switch (data.type) {
      case 'chart_update':
        this.emit('chartUpdate', {
          symbol: data.symbol,
          price: data.price,
          timestamp: data.timestamp
        });
        break;
      
      case 'price_update':
        this.emit('priceUpdate', {
          symbol: data.symbol,
          price: data.price,
          timestamp: data.timestamp
        });
        break;
      
      default:
        console.log('🤷 未知消息类型:', data.type);
    }
  }

  /**
   * 处理重连逻辑
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ WebSocket重连次数超限，停止重连');
      this.emit('maxReconnectAttemptsReached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`🔄 WebSocket重连中... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect();
    }, this.reconnectInterval);
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * 添加事件监听器
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * 移除事件监听器
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ 事件处理器错误 (${event}):`, error);
        }
      });
    }
  }

  /**
   * 获取连接状态
   */
  getConnectionState() {
    if (!this.ws) return 'CLOSED';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'OPEN';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }
}

// 创建单例实例
const websocketService = new WebSocketService();

export default websocketService;
